import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import CustomerNavigation from '../../components/CustomerNavigation';
import KakaoChatButton from '../../components/KakaoChatButton';
import CustomerInfoSection from './components/CustomerInfoSection';
import ItemsSection from './components/ItemsSection';
import PickupPreferencesSection from './components/PickupPreferencesSection';
import ConsentSection from './components/ConsentSection';
import OrderSummaryCard from './components/OrderSummaryCard';
import ProgressIndicator from './components/ProgressIndicator';
import SuccessModal from './components/SuccessModal';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabaseClient';

const CleaningRequestForm = () => {
  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerAddress: '',
    pickupType: '',
    customInstructions: '',
    kakaoConsent: true,
    termsConsent: false
  });

  const [items, setItems] = useState([
    { id: Date.now(), serviceId: '', priceMode: 'base', memo: '' }
  ]);

  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState('');

  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [submitError, setSubmitError] = useState('');

  const steps = [
    { id: 'customer', label: '고객 정보' },
    { id: 'items', label: '신발 정보' },
    { id: 'pickup', label: '픽업 선호사항' },
    { id: 'consent', label: '동의 사항' }
  ];

  useEffect(() => {
    const completedSteps = [];
    if (formData?.customerName && formData?.customerPhone && formData?.customerAddress) {
      completedSteps?.push(0);
    }
    if (items?.some(item => item?.serviceId)) {
      completedSteps?.push(1);
    }
    if (formData?.pickupType) {
      completedSteps?.push(2);
    }
    if (formData?.termsConsent) {
      completedSteps?.push(3);
    }
    setCurrentStep(completedSteps?.length);
  }, [formData, items]);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      setServicesLoading(true);
      setServicesError('');

      try {
        const { data, error } = await supabase
          .from('services')
          .select('id, name, base_price, promotional_price, active, created_at')
          .eq('active', true)
          .order('created_at', { ascending: true });

        if (error) throw error;
        if (!mounted) return;

        setServices(data || []);
      } catch (e) {
        if (!mounted) return;
        setServices([]);
        setServicesError(e?.message || '서비스 목록을 불러오지 못했습니다.');
      } finally {
        if (!mounted) return;
        setServicesLoading(false);
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;

    if (name === 'customerPhone') {
      const digits = (value || '').replace(/\D/g, '');
      setFormData(prev => ({ ...prev, [name]: digits }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddItem = () => {
    setItems(prev => [...prev, { id: Date.now(), serviceId: '', priceMode: 'base', memo: '' }]);
  };

  const handleUpdateItem = (index, field, value) => {
    setItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated?.[index], [field]: value };
      return updated;
    });
    if (errors?.items) {
      setErrors(prev => ({ ...prev, items: '' }));
    }
  };

  const handleRemoveItem = (index) => {
    setItems(prev => prev?.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    const serviceMap = new Map((services || []).map((s) => [s.id, s]));

    return (items || []).reduce((sum, item) => {
      if (!item?.serviceId) return sum;
      const svc = serviceMap.get(item.serviceId);
      if (!svc) return sum;

      const base = Number(svc?.base_price || 0);
      const promo = Number(svc?.promotional_price || 0);
      const hasPromo = promo > 0 && promo < base;
      return sum + (hasPromo ? promo : base);
    }, 0);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.customerName?.trim()) {
      newErrors.customerName = '이름을 입력해주세요';
    }

    const phoneRegex = /^01\d{8,9}$/;
    if (!formData?.customerPhone?.trim()) {
      newErrors.customerPhone = '전화번호를 입력해주세요';
    } else if (!phoneRegex?.test(formData?.customerPhone)) {
      newErrors.customerPhone = '올바른 전화번호 형식이 아닙니다 (예: 01012345678)';
    }

    if (!formData?.customerAddress?.trim()) {
      newErrors.customerAddress = '주소를 입력해주세요';
    }

    const hasSelectedItems = items?.some(item => item?.serviceId);
    if (!hasSelectedItems) {
      newErrors.items = '최소 1개 이상의 신발 서비스를 선택해주세요';
    }

    if (!formData?.pickupType) {
      newErrors.pickupType = '픽업 방법을 선택해주세요';
    }

    if (formData?.pickupType === 'custom' && !formData?.customInstructions?.trim()) {
      newErrors.customInstructions = '상세 요청사항을 입력해주세요';
    }

    if (!formData?.termsConsent) {
      newErrors.termsConsent = '이용약관에 동의해주세요';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const newOrderNumber = `ORD${Date.now()?.toString()?.slice(-8)}`;
      const orderDate = new Date()?.toISOString();
      const totalAmount = calculateTotal();

      const serviceMap = new Map((services || []).map((s) => [s.id, s]));

      const { data: createdOrder, error: createError } = await supabase
        .from('orders')
        .insert({
          order_number: newOrderNumber,
          customer_name: formData?.customerName,
          phone: formData?.customerPhone,
          address: formData?.customerAddress,
          status: '주문 접수',
          order_date: orderDate,
          total_amount: totalAmount,
          pickup_type: formData?.pickupType,
          custom_instructions: formData?.pickupType === 'custom' ? formData?.customInstructions : null,
          kakao_notification: !!formData?.kakaoConsent,
        })
        .select('id, order_number')
        .single();

      if (createError) throw createError;
      if (!createdOrder?.id) throw new Error('주문 생성에 실패했습니다.');

      const selectedItems = items?.filter((item) => item?.serviceId);
      const orderItemsRows = (selectedItems || [])
        .map((item) => {
          const svc = serviceMap.get(item.serviceId);
          if (!svc) return null;

          const base = Number(svc?.base_price || 0);
          const promo = Number(svc?.promotional_price || 0);
          const hasPromo = promo > 0 && promo < base;

          return {
            order_id: createdOrder.id,
            brand: '',
            model: '',
            service_type: svc?.name,
            price: hasPromo ? promo : base,
            memo: item?.memo || null,
          };
        })
        .filter(Boolean);

      if (orderItemsRows.length > 0) {
        const { error: itemsError } = await supabase
          .from('order_items')
          .insert(orderItemsRows);
        if (itemsError) throw itemsError;
      }

      if (formData?.kakaoConsent) {
        try {
          await supabase.functions.invoke('send-order-accepted', {
            body: { order_id: createdOrder.id },
          });
        } catch (notifyError) {
          console.warn('주문접수 알림톡 발송 실패:', notifyError);
        }
      }

      setOrderNumber(createdOrder.order_number || newOrderNumber);
      setShowSuccessModal(true);
    } catch (e) {
      setSubmitError(e?.message || '주문 등록에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    setFormData({
      customerName: '',
      customerPhone: '',
      customerAddress: '',
      pickupType: '',
      customInstructions: '',
      kakaoConsent: true,
      termsConsent: false
    });
    setItems([{ id: Date.now(), serviceId: '', priceMode: 'base' }]);
    setErrors({});
    setCurrentStep(0);
  };

  const totalPrice = calculateTotal();

  return (
    <>
      <Helmet>
        <title>세척 신청 - SneakerCare Pro</title>
        <meta name="description" content="전문 신발 세척 서비스를 신청하세요. 픽업부터 배송까지 편리하게 이용하실 수 있습니다." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <CustomerNavigation />

        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <h1 className="mb-2 text-3xl font-bold text-foreground">세척 서비스 신청</h1>
            <p className="text-muted-foreground text-white">
              신발 세척 서비스를 신청하시려면 아래 정보를 입력해주세요
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6">
                <CustomerInfoSection
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                />

                <ItemsSection
                  items={items}
                  services={services}
                  servicesLoading={servicesLoading}
                  servicesError={servicesError}
                  onAddItem={handleAddItem}
                  onUpdateItem={handleUpdateItem}
                  onRemoveItem={handleRemoveItem}
                  errors={errors}
                />

                <PickupPreferencesSection
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                />

                <ConsentSection
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                />

                {submitError && (
                  <div className="rounded-lg bg-error/10 p-4">
                    <p className="text-sm font-medium text-error">{submitError}</p>
                  </div>
                )}

                <div>
                  <Button
                    type="submit"
                    variant="default"
                    size="xl"
                    fullWidth
                    loading={isSubmitting}
                    iconName="Send"
                    iconPosition="right"
                    className="bg-blue-600 text-white hover:bg-blue-700"
                  >
                    주문 신청하기
                  </Button>
                </div>
              </form>
            </div>

            <div className="space-y-6">
              <ProgressIndicator currentStep={currentStep} steps={steps} />
              <OrderSummaryCard items={items} totalPrice={totalPrice} services={services} />
            </div>
          </div>
        </main>

        <SuccessModal
          isOpen={showSuccessModal}
          orderNumber={orderNumber}
          onClose={handleCloseModal}
        />

        <KakaoChatButton />
      </div>
    </>
  );
};

export default CleaningRequestForm;